import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import CustomScroll from "react-custom-scroll";

import GoalListItem from "./GoalListItem";
import { goalColumns } from "../admin/helper/multiselectColumns";
import { ensureType } from "../../hooks/ensureType";
import { IListDataItem, IChannelData } from "../../constants/interfaces";

const MAX_LEGEND_ITEMS = 5;

const fieldsHelper: any = {
	pageviews: "total",
	apppageviews: "totalApp",
	webpageviews: "totalWeb",
	conversion: "conversion",
	totalReadingTime: "readingTimeTotal",
};

const GoalListWidget = () => {
	let data = useSelector((state: any) => state.goal.data.stories) || [];
	const isLoading = useSelector((state: any) => state.goal.isLoading);
	const [initialLoad, setInitialLoad] = useState(true);
	useEffect(() => {
		if (!isLoading) setInitialLoad(false);
	}, [isLoading]);
	const goalMetricType = useSelector((state: any) => state.goal.data.metricType);
	const fields: Array<string> = ["title", fieldsHelper[goalMetricType]];
	const variant: any = "compact";
	let numberFields = fields.filter((field) => !["title", "section", "brandName", "channels"].includes(field));
	const brandName = fields.find((field) => field === "brandName");
	const section = fields.find((field) => field === "section");
	const title = "Biggest stories on reach";
	const showNotifications = false;

	if (initialLoad) return <div>Loading...</div>;
	// gather all channel types
	const storyChannels = data && Array.isArray(data) ? data.map((p: any) => p.channels ?? []) : [];
	const allChannels = storyChannels.reduce(
		(p: Array<IChannelData>, n: any[]) => p.concat(n.filter((c: { total: any }) => (c.total || 0) > 0)),
		[]
	);
	const channelLabels = Array.from(new Set(allChannels.map((c: { label: any }) => c.label)).values());
	const channelTypes = channelLabels.map((label) => {
		const { color } = ensureType(allChannels.find((e: { label: unknown }) => e.label === label));
		return { label, color };
	}, {});
	const channelMaxes: any = channelLabels.reduce((store: any, label: any) => {
		store[label] = Math.max(
			...allChannels.filter((c: { label: unknown }) => c.label === label).map((c: { total: any }) => c.total)
		);
		return store;
	}, {});
	const legendChannels = channelLabels
		.sort((a: any, b: any) => channelMaxes[b] - channelMaxes[a])
		.slice(0, MAX_LEGEND_ITEMS);
	// List item lines
	const totals = data && Array.isArray(data) ? data.map((item: { totalPageviews: any }) => item.totalPageviews) : [];
	const maxReach = totals.length > 0 ? totals.reduce((a: number, b: number) => Math.max(a, b)) : 0;

	const getLineWidth = (totalPageViews: number) => {
		return (totalPageViews / maxReach) * 100;
	};

	const getLineSum = (data: Array<{ total: number }>) => {
		return data.reduce((a, b) => a + (b.total || 0), 0);
	};

	const getFieldTitle = (field: keyof typeof goalColumns) => {
		return goalColumns[field];
	};

	return (
		<div className={`list-widget ${data.length > 10 ? "scroll-enabled" : ""}`}>
			<div className="list-widget__heading">
				<div className="list-widget__title">{title}</div>

				<div className="list-widget__heading-right">
					<div style={{ flex: 1 }} />
					<ul className="legend">
						{channelTypes
							.filter((t) => legendChannels.includes(t.label))
							.map((t: any) => (
								<li key={t.label} className="legend__item" style={{ color: t.color }}>
									<span>{t.label}</span>
								</li>
							))}
					</ul>
					{/*{variant === 'full' ? setBrand() : null}*/}
				</div>
			</div>
			<span className="list-widget__extra-info"></span>
			<div className="list-widget__list">
				<div className="list-widget__fields">
					<div className="list-widget__heading-header" />
					<div
						className={`list-widget__heading-headers dynamic-flex-${
							numberFields.length + Number(!!section) + (isNaN(Number(brandName)) ? 0 : 1)
						}`}
					>
						{data.length > 0 &&
							numberFields.map((field, index) => (
								<div
									className={`list-widget-stat-heading ${field === "pubdate" ? "date" : ""}`}
									key={index}
								>
									{getFieldTitle(field as keyof typeof goalColumns)}
								</div>
							))}
						{variant === "full" && !!section && (
							<div className="list-widget-stat-heading list-widget__heading-extra">Section</div>
						)}
						{!!brandName && (
							<div className="list-widget-stat-heading list-widget-stat-heading-brand-name">Brand</div>
						)}
					</div>
				</div>
				<CustomScroll heightRelativeToParent="calc(100% - 55px)">
					{data.length > 0 ? (
						(data || []).map((item: IListDataItem) => {
							return (
								<GoalListItem
									key={`${item.relationId}_${item.brandId}`}
									data={item}
									lineWidth={getLineWidth(item.totalPageviews)}
									lineSum={getLineSum(item.channels)}
									variant={variant}
									numberFields={numberFields}
									showBrand={!!brandName}
									showSection={!!section}
									showNotifications={showNotifications}
								/>
							);
						})
					) : (
						<span>No data</span>
					)}
				</CustomScroll>
			</div>
		</div>
	);
};

export default GoalListWidget;
